'use client'

import { Button } from '@/components/ui/button'
import { ArrowRight, Zap, TrendingUp, Sparkles } from 'lucide-react'
import { AnimatedGridPattern } from '@/components/ui/animated-grid-pattern'
import { AutomotiveCommandVisual } from '@/components/ui/automotive-command-visual'
import { cn } from '@/lib/utils'

interface AutomotiveHeroProps {
  onDemoClick: () => void
  bookingUrl: string
  eyebrow?: string
  title?: string
  description?: string
  supporting?: string
  primaryCta?: string
  secondaryCta?: string
}

export function AutomotiveHero({
  onDemoClick,
  bookingUrl,
  eyebrow,
  title,
  description,
  supporting,
  primaryCta,
  secondaryCta,
}: AutomotiveHeroProps) {
  return (
    <section className="relative overflow-hidden bg-[#101319] text-white font-instrument">
      {/* Animated grid, brand blue, faded to the edges */}
      <AnimatedGridPattern
        numSquares={50}
        maxOpacity={0.25}
        duration={3}
        repeatDelay={1}
        className={cn(
          "[mask-image:radial-gradient(700px_circle_at_center,white,transparent)]",
          "fill-[#2F5FE8]/25 stroke-[#2F5FE8]/15"
        )}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#2F5FE8]/10 to-transparent pointer-events-none" />

      <div className="container mx-auto px-6 pt-32 pb-16 lg:pt-44 lg:pb-24 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase mb-6 text-[#7EA0F5] animate-fade-in">
              <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
              {eyebrow ?? 'Automotive Growth Systems'}
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-[-0.03em] font-semibold mb-6 text-pretty animate-fade-in-up">
              {title ?? (
                <>
                  Every Missed Lead Is a Deal Your{' '}
                  <span className="text-[#7EA0F5]">Competitor Just Closed.</span>
                </>
              )}
            </h1>

            <p className="text-lg leading-relaxed text-[#B9C1CE] mb-4 animate-fade-in-up animation-delay-200">
              {description ??
                'Frayze answers every lead in minutes, books the test drive, and runs the entire follow-up system for you, so slow replies, dropped follow-ups, and messy CRMs stop costing you deals.'}
            </p>

            <p className="text-base leading-relaxed text-[#9BA3B2] mb-8 animate-fade-in-up animation-delay-300">
              {supporting ??
                'Built by an operator who ran the lead engine across a 6-rooftop dealer group, not a SaaS vendor. For independent dealers, finance-first teams, and multi-rooftop groups.'}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 p-6 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm mb-10 animate-fade-in-up animation-delay-400">
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-white mb-1">Minutes</div>
                <div className="font-mono-ibm text-[10px] text-[#9BA3B2] uppercase tracking-[0.12em]">To every new lead</div>
              </div>
              <div className="text-center lg:text-left border-x border-white/10">
                <div className="text-3xl font-bold text-white mb-1">6 rooftops</div>
                <div className="font-mono-ibm text-[10px] text-[#9BA3B2] uppercase tracking-[0.12em]">Operator-built</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-white mb-1">24/7</div>
                <div className="font-mono-ibm text-[10px] text-[#9BA3B2] uppercase tracking-[0.12em]">AI follow-up</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12 animate-fade-in-up animation-delay-500">
              <Button
                size="lg"
                className="text-base font-bold px-8 h-14 rounded-xl bg-[#2F5FE8] hover:bg-[#2049BE] text-white shadow-lg shadow-[#2F5FE8]/25 transition-all hover:-translate-y-0.5 active:translate-y-0 group"
                onClick={onDemoClick}
              >
                {primaryCta ?? 'See it in action'}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="text-base font-bold px-8 h-14 rounded-xl border-[#6B7484] bg-transparent text-white hover:border-white hover:bg-white/5 hover:text-white"
              >
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                  {secondaryCta ?? 'Book a 20-min consult'}
                </a>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-[#9BA3B2] animate-fade-in-up animation-delay-600">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#7EA0F5]" />
                <span>Quick Setup</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#7EA0F5]" />
                <span>Proven Results</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#7EA0F5]" />
                <span>AI-Powered</span>
              </div>
            </div>
          </div>

          {/* Right Side: dealer lead handled end-to-end, on loop */}
          <div className="relative hidden lg:block animate-fade-in-up animation-delay-300">
            <AutomotiveCommandVisual />
          </div>
        </div>
      </div>
    </section>
  )
}
